import { inject, Injectable, signal } from '@angular/core';
import { StorageService } from './storage.service';
import {
    audioEnabledKey,
    soundOnboardingSeenKey,
    userStorageScope,
} from './storage-keys';

/**
 * Persistent, per-user audio preference for manual approval alert sounds.
 *
 * Backed by localStorage so the preference survives sessions (AB#8659).
 * Keys are suffixed with the logged-in user's id ("anonymous" fallback) so
 * different users on the same machine keep independent preferences.
 */
@Injectable({
    providedIn: 'root',
})
export class AudioPreferenceService {
    private readonly storageService = inject(StorageService);

    private readonly audioEnabledState = signal<boolean>(
        this.readAudioEnabled(),
    );
    private readonly onboardingSeenState = signal<boolean>(
        this.readOnboardingSeen(),
    );

    /**
     * Monotonic counter incremented every time the user engages with a held
     * bet (opens it for review or takes a decision), signalling that the
     * currently-playing alert should be silenced (AB#8756). It is a per-
     * interaction event, not persisted, and deliberately separate from the
     * global on/off preference: silencing the current alert never changes
     * whether future alerts are allowed to play.
     */
    private readonly silenceRequestState = signal<number>(0);

    /** Whether approval alert sounds are enabled. Defaults to on. */
    readonly audioEnabled = this.audioEnabledState.asReadonly();

    /** Whether the one-time sound onboarding popover has been shown. */
    readonly onboardingSeen = this.onboardingSeenState.asReadonly();

    /**
     * Read-only silence-request counter. The alert player (navbar) reacts to
     * increments of this signal by stopping the current alert without
     * touching {@link audioEnabled}, so a genuinely new held bet arriving
     * afterwards still re-triggers the sound (AB#8756).
     */
    readonly silenceRequest = this.silenceRequestState.asReadonly();

    toggleAudioEnabled(): void {
        this.setAudioEnabled(!this.audioEnabledState());
    }

    /**
     * Requests that the currently-playing alert be silenced. Called from the
     * review-open navigation and the Approve/Reject/Open decision handlers
     * (AB#8756). Does NOT mutate the global on/off preference.
     */
    silenceCurrentAlert(): void {
        this.silenceRequestState.update((count) => count + 1);
    }

    setAudioEnabled(enabled: boolean): void {
        this.audioEnabledState.set(enabled);
        const key = this.audioEnabledKey();
        // AB#9774 / UI-271: no resolvable user id => in-memory only, never a
        // shared bucket that would let one trader's mute govern another's
        // alerts. See userStorageScope().
        if (key === null) {
            return;
        }
        localStorage.setItem(key, String(enabled));
    }

    markOnboardingSeen(): void {
        this.onboardingSeenState.set(true);
        const key = this.onboardingSeenKey();
        if (key === null) {
            return;
        }
        localStorage.setItem(key, 'true');
    }

    /**
     * Re-reads the audio-enabled and onboarding-seen flags from localStorage
     * for whichever user is *now* identified by {@link StorageService}
     * (AB#9726 / UI-231). This service is a `providedIn: 'root'` singleton
     * read once at construction, so without this it keeps governing the
     * previous user's alerts across a same-tab logout/login — on a shared
     * trading-floor terminal, user A's sound preference would silently
     * control user B's alerts. `AuthService` calls this on logout and after
     * a successful login, mirroring the SignalRService (AB#8980) /
     * SavedFiltersService (AB#8985) fix pattern. Does not touch
     * {@link silenceRequest}: that counter is a non-persisted per-interaction
     * event, not per-user state.
     */
    reloadForCurrentUser(): void {
        this.audioEnabledState.set(this.readAudioEnabled());
        this.onboardingSeenState.set(this.readOnboardingSeen());
    }

    private readAudioEnabled(): boolean {
        const key = this.audioEnabledKey();
        const stored = key === null ? null : localStorage.getItem(key);
        return stored === null ? true : stored === 'true';
    }

    private readOnboardingSeen(): boolean {
        const key = this.onboardingSeenKey();
        return key !== null && localStorage.getItem(key) === 'true';
    }

    /** `null` when the preference must not be persisted — see {@link userScope}. */
    private audioEnabledKey(): string | null {
        const scope = this.userScope();
        return scope === null ? null : audioEnabledKey(scope);
    }

    private onboardingSeenKey(): string | null {
        const scope = this.userScope();
        return scope === null ? null : soundOnboardingSeenKey(scope);
    }

    /**
     * `null` means "a user is logged in but the backend sent no
     * `dataPulseUserId`" — persist nothing rather than share one alert-sound
     * setting across every user of that deployment (AB#9774 / UI-271).
     */
    private userScope(): string | null {
        return userStorageScope(this.storageService.getUser());
    }
}
